import { useState, useEffect } from 'react';
import { FiX, FiDatabase, FiCheckCircle, FiSearch } from 'react-icons/fi';
import { userRequest } from '../../../utils/requestMethods';

const TYPE_BADGES = {
  multiple_choice: { label: 'MCQ',       bg: 'bg-blue-100',   text: 'text-blue-700'   },
  true_or_false:   { label: 'True/False', bg: 'bg-purple-100', text: 'text-purple-700' },
};

const DIFF_BADGES = {
  easy:   { label: 'Easy',   bg: 'bg-green-100', text: 'text-green-700' },
  medium: { label: 'Medium', bg: 'bg-amber-100',  text: 'text-amber-700' },
  hard:   { label: 'Hard',   bg: 'bg-red-100',   text: 'text-red-700'   },
};

// Deduplicate by normalized_name, keep first entry
const dedupe = (arr, key) =>
  arr.reduce((acc, item) => {
    if (!acc.find(x => x[key] === item[key])) acc.push(item);
    return acc;
  }, []);

// Detect if a string contains HTML markup
const isHtml = (str) => /<[a-z][\s\S]*>/i.test(str || '');

const mapToAppQuestion = (q, index) => ({
  id: Date.now() + index,
  question: q.question_text,  // Keep raw HTML — preserves images and formatting
  type: q.question_type,
  options: q.options || [],
  correct_answer: q.correct_answer,
  is_active: true,
  point: undefined,
});

const NappsImportModal = ({ onClose, onConfirm, testId, schoolId }) => {
  const [classes, setClasses]           = useState([]);
  const [subjects, setSubjects]         = useState([]);
  const [metaLoading, setMetaLoading]   = useState(true);

  const [selectedClassId,   setSelectedClassId]   = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  const [questions,   setQuestions]   = useState([]);
  const [qLoading,    setQLoading]    = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirming,  setConfirming]  = useState(false);

  const [search,           setSearch]           = useState('');
  const [filterType,       setFilterType]       = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterGroup,      setFilterGroup]      = useState('');

  // Fetch classes + subjects once on mount
  useEffect(() => {
    const fetch = async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          userRequest.get('/napps/classes?limit=100'),
          userRequest.get('/napps/subjects?limit=100'),
        ]);
        setClasses(dedupe(cRes.data.data || [], 'normalized_name'));
        setSubjects(dedupe(sRes.data.data || [], 'normalized_name'));
      } catch (err) {
        console.error('NABS meta fetch error:', err);
      } finally {
        setMetaLoading(false);
      }
    };
    fetch();
  }, []);

  // Fetch questions whenever class, subject, or group changes
  useEffect(() => {
    if (!selectedClassId || !selectedSubjectId) {
      setQuestions([]);
      setSelectedIds(new Set());
      return;
    }
    const fetch = async () => {
      setQLoading(true);
      try {
        const groupParam = filterGroup ? `&grouping=${filterGroup}` : '';
        const { data } = await userRequest.get(
          `/napps/questions?class_id=${selectedClassId}&subject_id=${selectedSubjectId}&limit=100${groupParam}`
        );
        setQuestions(data.data || []);
        setSelectedIds(new Set());
      } catch (err) {
        console.error('NABS questions fetch error:', err);
      } finally {
        setQLoading(false);
      }
    };
    fetch();
  }, [selectedClassId, selectedSubjectId, filterGroup]);

  const filteredQuestions = questions.filter(q => {
    if (filterType       && q.question_type !== filterType)       return false;
    if (filterDifficulty && q.difficulty    !== filterDifficulty) return false;
    if (search && !q.question_text.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const allSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedIds.has(q._id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) filteredQuestions.forEach(q => next.delete(q._id));
      else             filteredQuestions.forEach(q => next.add(q._id));
      return next;
    });
  };

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);

    const selected = filteredQuestions.filter(q => selectedIds.has(q._id));

    // Log the import (fire-and-forget — don't block if it fails)
    if (testId && schoolId) {
      userRequest.post('/test/import-questions', {
        test_id: testId,
        school_id: schoolId,
        source_question_ids: selected.map(q => q._id),
      }).catch(err => console.error('NABS import log failed:', err));
    }

    onConfirm(selected.map((q, i) => mapToAppQuestion(q, i)));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-xl">
              <FiDatabase className="text-indigo-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">NAPPS Question Bank</h2>
              <p className="text-xs text-gray-400">Import questions from the NAPPS question bank</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">

          {/* Class + Subject + Group selectors */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Class</label>
              <select
                value={selectedClassId}
                onChange={e => setSelectedClassId(e.target.value)}
                disabled={metaLoading}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
              >
                <option value="">Select class...</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
              <select
                value={selectedSubjectId}
                onChange={e => setSelectedSubjectId(e.target.value)}
                disabled={metaLoading}
                className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
              >
                <option value="">Select subject...</option>
                {subjects.map(sub => (
                  <option key={sub._id} value={sub._id}>{sub.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Grouping</label>
            <select
              value={filterGroup}
              onChange={e => setFilterGroup(e.target.value)}
              className="w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
            >
              <option value="">All groups</option>
              <option value="mid_term">Mid Term</option>
              <option value="exam">Exam</option>
            </select>
          </div>

          {/* Filters — only shown once questions are loaded */}
          {questions.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search questions..."
                  className="pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">All types</option>
                <option value="multiple_choice">MCQ</option>
                <option value="true_or_false">True / False</option>
              </select>
              <select
                value={filterDifficulty}
                onChange={e => setFilterDifficulty(e.target.value)}
                className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">All difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          )}

          {/* Question list */}
          {qLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : !selectedClassId || !selectedSubjectId ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400">
              <FiDatabase className="w-10 h-10 mb-3 text-gray-200" />
              <p className="text-sm">Select a class and subject to browse available questions</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              No questions found for this selection
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQuestions.map(q => {
                const isSelected = selectedIds.has(q._id);
                const typeBadge = TYPE_BADGES[q.question_type] || TYPE_BADGES.multiple_choice;
                const diffBadge = DIFF_BADGES[q.difficulty]    || DIFF_BADGES.easy;
                return (
                  <button
                    key={q._id}
                    type="button"
                    onClick={() => toggleSelect(q._id)}
                    className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                    }`}>
                      {isSelected && <FiCheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isHtml(q.question_text) ? (
                        <div
                          className="text-sm text-gray-800 leading-snug prose prose-sm max-w-none line-clamp-4"
                          dangerouslySetInnerHTML={{ __html: q.question_text }}
                        />
                      ) : (
                        <p className="text-sm text-gray-800 leading-snug line-clamp-4">{q.question_text}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeBadge.bg} ${typeBadge.text}`}>
                          {typeBadge.label}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${diffBadge.bg} ${diffBadge.text}`}>
                          {diffBadge.label}
                        </span>
                        {q.topic && (
                          <span className="text-xs text-gray-400">{q.topic}</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            {filteredQuestions.length > 0 && (
              <button type="button" onClick={toggleSelectAll} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
            {selectedIds.size > 0 && (
              <span className="text-xs text-gray-400">{selectedIds.size} selected</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0 || confirming}
              className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              {confirming ? 'Adding...' : `Add Selected (${selectedIds.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NappsImportModal;
